import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";

import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

function formatDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("el-GR");
}

const PAGE_SIZE = 100;

export default function MyOffersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${config.BACKEND_BASE_URL}/api/admin/offers/mine`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOffers(res.data.offers || []);
      } catch {
        setError(t("eventRequests.loadError"));
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const sorted = useMemo(
    () => [...offers].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [offers]
  );
  const pageCount = Math.ceil(sorted.length / PAGE_SIZE);
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <DashboardLayout>
      <Box py={3} sx={{ color: "#fff" }}>
        <Card sx={{ p: 3, color: "#fff", borderRadius: 3 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1} mb={3}>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#fff", mb: 0.5, wordBreak: "break-word" }}>
                {t("eventRequests.myOffersTitle")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#fff", opacity: 0.7, wordBreak: "break-word" }}>
                {t("eventRequests.myOffersSubtitle")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/pages/event-requests")}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {"←"} {t("eventRequests.backToRequests")}
            </Button>
          </Stack>

          {loading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          )}

          {!loading && !error && offers.length === 0 && (
            <Box py={6} textAlign="center">
              <Typography variant="body1" sx={{ color: "#fff", opacity: 0.7, wordBreak: "break-word" }}>
                {t("eventRequests.noOffers")}
              </Typography>
            </Box>
          )}

          {!loading && !error && paged.map((offer) => {
            const req = offer.eventRequest;
            const eventDateRange = req?.eventDate?.from
              ? req.eventDate.to && req.eventDate.to !== req.eventDate.from
                ? `${formatDate(req.eventDate.from)} – ${formatDate(req.eventDate.to)}`
                : formatDate(req.eventDate.from)
              : "—";

            return (
              <Card
                key={offer._id}
                sx={{ mb: 2, p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: 3, border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
                  <Box flex={1} minWidth={0}>
                    <Box mb={0.5}>
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            ["kept", "accepted"].includes(offer.status) ? "#81c784"
                            : offer.status === "rejected" ? "#e57373"
                            : offer.status === "viewed" ? "#64b5f6"
                            : "rgba(255,255,255,0.55)",
                          fontStyle: "italic",
                          display: "block",
                          wordBreak: "break-word",
                        }}
                      >
                        {["new", "sent"].includes(offer.status)
                          ? t("eventRequests.offerLabelNew")
                          : offer.status === "viewed"
                          ? t("eventRequests.offerLabelViewed")
                          : ["kept", "accepted"].includes(offer.status)
                          ? t("eventRequests.offerLabelKept")
                          : t("eventRequests.offerLabelRejected")}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff", wordBreak: "break-word" }}>
                        {req?.title || req?.occasion?.nameGr || req?.occasion?.nameEn || "—"}
                      </Typography>
                    </Box>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 2 }} flexWrap="wrap" alignItems="flex-start">
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", wordBreak: "break-word" }}>
                        <strong style={{ color: "#fff" }}>{t("eventRequests.colCity")}:</strong>{" "}
                        {req?.city || "—"}
                      </Typography>
                      {req?.userName && (
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", wordBreak: "break-word" }}>
                          <strong style={{ color: "#fff" }}>{t("eventRequests.colRequester")}:</strong>{" "}
                          {req.userName}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", wordBreak: "break-word" }}>
                        <strong style={{ color: "#fff" }}>{t("eventRequests.colEventDate")}:</strong>{" "}
                        {eventDateRange}
                      </Typography>
                      {offer.price != null && (
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", wordBreak: "break-word" }}>
                          <strong style={{ color: "#fff" }}>{t("eventRequests.colPrice")}:</strong>{" "}
                          {"€"}{offer.price}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", wordBreak: "break-word" }}>
                        <strong style={{ color: "#fff" }}>{t("eventRequests.colCategory")}:</strong>{" "}
                        {offer.category}
                      </Typography>
                    </Stack>

                    {offer.message && (
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mt: 0.5, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                        {offer.message}
                      </Typography>
                    )}

                    {offer.contactTimes && (
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mt: 0.25, wordBreak: "break-word" }}>
                        <strong style={{ color: "rgba(255,255,255,0.8)" }}>{t("eventRequests.fieldContactTimes")}:</strong>{" "}
                        {offer.contactTimes}
                      </Typography>
                    )}

                    {req?.status && (
                      <Typography
                        variant="caption"
                        sx={{ color: req.status === "open" ? "#81c784" : "#ffa726", fontStyle: "italic", display: "block", mt: 0.25, wordBreak: "break-word" }}
                      >
                        {req.status === "open" ? t("eventRequests.reqLabelOpen") : t("eventRequests.reqLabelClosed")}
                      </Typography>
                    )}
                  </Box>

                  <Stack spacing={1} alignItems={{ md: "flex-end" }} flexShrink={0}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", whiteSpace: "nowrap" }}>
                      {formatDate(offer.createdAt)}
                    </Typography>
                  </Stack>
                </Stack>
              </Card>
            );
          })}

          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                sx={{ "& .MuiPaginationItem-root": { color: "#fff" } }}
              />
            </Box>
          )}
        </Card>
      </Box>
    </DashboardLayout>
  );
}
